import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { downloadBlob, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ContractForm from "@/components/contract/ContractForm";
import SignedPanel from "@/components/contract/SignedPanel";

const STEPS = { draft: "Bozza", generated: "Generato", signed: "Firmato", sent: "Inviato in produzione" };

export default function ContractPage() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [contract, setContract] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [o, c] = await Promise.all([api.get(`/offers/${offerId}`), api.get(`/offers/${offerId}/contract`)]);
        setOffer(o.data);
        setContract(c.data);
        setData(c.data.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        navigate(`/offerte/${offerId}`);
      }
    })();
  }, [offerId]);

  const save = async () => {
    const { data: c } = await api.put(`/offers/${offerId}/contract`, data);
    setContract(c);
    return c;
  };

  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch {}
      }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setBusy("");
    }
  };

  const onSave = () => act("save", async () => { await save(); toast.success("Dati contratto salvati"); });
  const onPdf = () => act("pdf", async () => {
    await save();
    await downloadBlob(`/offers/${offerId}/contract/pdf`, `contratto-${data.ragione_sociale || offer.client_name}.pdf`);
    setContract((c) => ({ ...c, status: c.status === "draft" ? "generated" : c.status }));
  });

  if (!contract || !data) return <div className="p-10 text-muted-foreground">Caricamento...</div>;
  const won = offer.status === "won";

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <button onClick={() => navigate(`/offerte/${offerId}`)} className="flex items-center gap-2 text-muted-foreground hover:text-navy font-semibold mb-6 transition-colors duration-200" data-testid="contract-back-button">
        <ArrowLeft size={18} /> Torna all'offerta
      </button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Contratto ufficiale</p>
          <h1 className="text-3xl font-extrabold text-navy tracking-tight">{offer.client_company || offer.client_name}</h1>
        </div>
        <span data-testid="contract-status-badge" className="px-3 py-1 rounded-full text-sm font-bold border bg-primary/10 text-primary border-primary/30">{STEPS[contract.status] || "Bozza"}</span>
      </div>

      {!won && <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="contract-not-won">Segna l'offerta come Vinta per generare il contratto.</div>}

      <div className="bg-white border border-border rounded-2xl p-6 md:p-8 mb-6">
        <h2 className="font-extrabold text-navy text-lg mb-6">1. Compila i dati</h2>
        <ContractForm data={data} set={setData} />
        <div className="flex flex-wrap gap-2 mt-8">
          <Button variant="outline" onClick={onSave} disabled={!!busy} data-testid="save-contract-button" className="gap-2 font-bold">
            {busy === "save" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salva
          </Button>
          <Button onClick={onPdf} disabled={!!busy || !won} data-testid="download-contract-button" className="gap-2 font-bold">
            {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Scarica contratto PDF
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl p-6 md:p-8">
        <h2 className="font-extrabold text-navy text-lg mb-1">2. Contratto firmato</h2>
        <p className="text-sm text-muted-foreground mb-6">Carica il contratto firmato dal cliente (PDF, Word o foto).</p>
        <SignedPanel offerId={offerId} contract={contract} setContract={setContract} isAdmin={user?.role === "admin"} />
      </div>
    </div>
  );
}
